import React from 'react';
import { Zap, AlertTriangle, Flag } from 'lucide-react';

/**
 * Timeline visual das fases, estilo release plan.
 *
 * Suporta fases sobrepostas (ex.: "Reta Final" e "Reta Finalíssima" rodando
 * juntas): empilha em swim lanes quando há overlap temporal, em vez de
 * comprimir lado a lado como se fossem sequenciais.
 *
 * Cada fase é posicionada por left/width absolutos proporcionais à duração
 * total, permitindo visualizar simultaneidade real.
 */
const MetasTimeline = ({ fases = [], darkMode }) => {
  if (!fases.length) return null;

  const hoje = new Date().toISOString().slice(0, 10);

  // Limites do timeline
  const toMs = (d) => new Date(d).getTime();
  const primeiroInicio = fases.reduce(
    (min, f) => (f.fase.data_inicio < min ? f.fase.data_inicio : min),
    fases[0].fase.data_inicio,
  );
  const ultimoFim = fases.reduce(
    (max, f) => (f.fase.data_fim > max ? f.fase.data_fim : max),
    fases[0].fase.data_fim,
  );
  const totalMs = toMs(ultimoFim) - toMs(primeiroInicio);
  if (totalMs <= 0) return null;

  // Posiciona cada fase em porcentagem
  const posicionar = (d) => ((toMs(d) - toMs(primeiroInicio)) / totalMs) * 100;
  const hojePct = Math.max(0, Math.min(100, posicionar(hoje)));

  // Swim lanes: atribui linha a cada fase, empilhando quando há overlap
  const fasesComLinha = alocarLinhas(fases);
  const totalLinhas = Math.max(1, ...fasesComLinha.map(f => f.linha + 1));
  const alturaLinha = 90; // px por swim lane
  const alturaTotal = totalLinhas * alturaLinha + 20; // +20 para datas

  const formatDate = (d) => {
    if (!d) return '';
    const [, m, dia] = (d.slice(0, 10)).split('-');
    return `${dia}/${m}`;
  };

  const diasEntre = (a, b) => Math.max(0, Math.round((toMs(b) - toMs(a)) / 864e5));
  const diasRestantes = (fim) => Math.max(0, diasEntre(hoje, fim));

  const card = darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200';
  const txt = darkMode ? 'text-white' : 'text-gray-900';
  const txtSec = darkMode ? 'text-gray-400' : 'text-gray-500';
  const txtMicro = darkMode ? 'text-gray-500' : 'text-gray-400';

  return (
    <div className={`rounded-xl border p-5 ${card}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-xs uppercase tracking-wide font-semibold ${txtSec}`}>
          Plano de entrega
        </h3>
        <span className={`text-xs ${txtSec}`}>
          {formatDate(primeiroInicio)} → {formatDate(ultimoFim)}
        </span>
      </div>

      {/* Timeline com posicionamento absoluto — sem scroll, cabe na tela */}
      <div className="relative" style={{ minHeight: `${alturaTotal}px` }}>
        {/* Linha base por swim lane */}
        {Array.from({ length: totalLinhas }).map((_, i) => (
          <div
            key={`lane-${i}`}
            className={`absolute left-0 right-0 h-px ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}
            style={{ top: `${i * alturaLinha + alturaLinha / 2}px` }}
          />
        ))}

        {/* Marcador de hoje */}
        <div
          className="absolute top-0 z-20"
          style={{ left: `${hojePct}%`, height: `${alturaTotal}px` }}
        >
          <div className="w-0.5 h-full bg-red-500 opacity-70" />
          <div className="absolute -top-0 left-1/2 -translate-x-1/2 whitespace-nowrap">
            <span className={`text-[9px] font-bold text-red-500 px-1 rounded ${
              darkMode ? 'bg-red-900/40' : 'bg-red-50'
            }`}>HOJE</span>
          </div>
        </div>

        {/* Blocos de fase — posicionados por left/width */}
        {fasesComLinha.map(({ faseData, linha }) => {
          const { fase, atual_fase, percentual, status } = faseData;
          const inicio = fase.data_inicio?.slice(0, 10);
          const fim = fase.data_fim?.slice(0, 10);
          const left = posicionar(inicio);
          const right = posicionar(fim);
          // Garante largura mínima legível sem estourar o container
          const widthRaw = right - left;
          const minW = 5; // % mínimo para o texto caber
          const width = Math.max(minW, widthRaw);
          // Se left + width ultrapassa 100%, recua o left para caber
          const leftFinal = left + width > 100 ? Math.max(0, 100 - width) : left;
          const pct = Math.min(100, Math.max(0, percentual || 0));

          const isAtiva = status !== 'nao_iniciada' && inicio <= hoje && fim >= hoje;
          const isCompleta = pct >= 100;
          const isFutura = status === 'nao_iniciada' || inicio > hoje;
          const isPassada = !isFutura && !isAtiva;

          const dias = diasRestantes(fim);
          const faltam = Math.max(0, (fase.meta_fase || 0) - (atual_fase || 0));
          const ritmo = dias > 0 && faltam > 0 ? Math.ceil(faltam / dias) : 0;

          const bgBlock = isCompleta
            ? darkMode ? 'bg-green-900/50 border-green-700' : 'bg-green-50 border-green-300'
            : isAtiva
              ? darkMode ? 'bg-blue-900/40 border-blue-600' : 'bg-blue-50 border-blue-300'
              : isPassada
                ? darkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-300'
                : darkMode ? 'bg-zinc-900/60 border-zinc-700' : 'bg-gray-50 border-gray-200';

          const fillColor = isCompleta ? 'bg-green-500' : isAtiva ? 'bg-blue-500' : isPassada ? 'bg-yellow-500' : 'bg-gray-400';

          return (
            <div
              key={fase.id}
              className={`absolute rounded-lg border px-2.5 py-2 overflow-hidden transition-all ${bgBlock}`}
              style={{
                left: `${leftFinal}%`,
                width: `${width}%`,
                top: `${linha * alturaLinha + 4}px`,
                height: `${alturaLinha - 12}px`,
              }}
              title={`${fase.nome}\n${formatDate(inicio)} — ${formatDate(fim)}\n${pct.toFixed(0)}% (${(atual_fase || 0).toLocaleString()} / ${(fase.meta_fase || 0).toLocaleString()})`}
            >
              {/* Fill */}
              <div
                className={`absolute inset-0 opacity-15 ${fillColor}`}
                style={{ width: `${pct}%` }}
              />

              {/* Conteúdo */}
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-1">
                    {isAtiva && <Zap size={9} className="text-blue-400 shrink-0" />}
                    {isCompleta && <Flag size={9} className="text-green-500 shrink-0" />}
                    <span className={`text-[10px] font-bold truncate ${txt}`}>{fase.nome}</span>
                  </div>

                  <div className={`text-sm font-bold leading-tight ${
                    isCompleta ? 'text-green-500' : isAtiva ? (darkMode ? 'text-blue-400' : 'text-blue-600') : txtSec
                  }`}>
                    {isFutura ? '—' : `${pct.toFixed(0)}%`}
                  </div>
                </div>

                <div className={`text-[9px] leading-tight ${txtSec}`}>
                  {isAtiva && ritmo > 0 && (
                    <span className={`font-medium ${
                      ritmo > 1500 ? 'text-red-500' : ritmo > 800 ? 'text-yellow-500' : darkMode ? 'text-green-400' : 'text-green-600'
                    }`}>{ritmo.toLocaleString('pt-BR')}/dia </span>
                  )}
                  {isAtiva && dias > 0 && <span>• {dias}d</span>}
                  {isPassada && !isCompleta && faltam > 0 && <span className="text-yellow-500">−{faltam.toLocaleString('pt-BR')}</span>}
                  {isFutura && <span>{formatDate(inicio)}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <div className={`flex flex-wrap gap-3 mt-3 pt-3 border-t text-[10px] ${
        darkMode ? 'border-zinc-700' : 'border-gray-200'
      } ${txtSec}`}>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Ativa</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Concluída</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Atrasada</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" /> Futura</span>
        <span className="flex items-center gap-1"><span className="w-0.5 h-3 bg-red-500" /> Hoje</span>
      </div>
    </div>
  );
};

/**
 * Aloca fases em swim lanes, empilhando quando há overlap temporal.
 *
 * Greedy: percorre por data_inicio e coloca cada fase na primeira linha que
 * esteja livre (fim da última fase naquela linha < inicio desta).
 */
function alocarLinhas(fases) {
  // Ordena por inicio, depois por duração (mais longas primeiro para estabilidade visual)
  const ordenadas = [...fases].sort((a, b) => {
    const d = a.fase.data_inicio.localeCompare(b.fase.data_inicio);
    if (d !== 0) return d;
    return b.fase.data_fim.localeCompare(a.fase.data_fim); // mais longa primeiro
  });

  const linhas = []; // cada entrada é a data_fim da última fase naquela linha

  return ordenadas.map((faseData) => {
    const inicio = faseData.fase.data_inicio?.slice(0, 10);
    const fim = faseData.fase.data_fim?.slice(0, 10);

    // Procura primeira linha livre
    let linhaUsada = linhas.findIndex(fimLinha => fimLinha < inicio);
    if (linhaUsada === -1) {
      linhaUsada = linhas.length;
      linhas.push(fim);
    } else {
      linhas[linhaUsada] = fim;
    }

    return { faseData, linha: linhaUsada };
  });
}

export default MetasTimeline;
